import { useState, useEffect } from 'react';
import { db } from 'lib/firebase';
import { 
    collection, 
    query, 
    where, 
    orderBy, 
    limit, 
    startAfter,
    onSnapshot, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc,
    serverTimestamp,
    getDocs
} from 'firebase/firestore';

/**
 * Custom hook for managing grocery list data with real-time Firestore sync
 */
export const useGroceryData = () => {
    const [activeItems, setActiveItems] = useState([]);
    const [historyItems, setHistoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [lastHistoryDoc, setLastHistoryDoc] = useState(null);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);

    // Real-time listener for active (unchecked) items
    useEffect(() => {
        const q = query(
            collection(db, 'groceryItems'),
            where('checked', '==', false),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setActiveItems(items);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching active grocery items:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Load initial history items (first 50)
    useEffect(() => {
        loadHistory(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadHistory = async (isInitial = false) => {
        if (!isInitial && !hasMoreHistory) return;
        
        setHistoryLoading(true);
        
        try {
            let q;
            if (isInitial || !lastHistoryDoc) {
                q = query(
                    collection(db, 'groceryItems'),
                    where('checked', '==', true),
                    orderBy('checkedAt', 'desc'),
                    limit(50)
                );
            } else {
                q = query(
                    collection(db, 'groceryItems'),
                    where('checked', '==', true),
                    orderBy('checkedAt', 'desc'),
                    startAfter(lastHistoryDoc),
                    limit(50)
                );
            }

            const snapshot = await getDocs(q);
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            if (isInitial) {
                setHistoryItems(items);
            } else {
                setHistoryItems(prev => [...prev, ...items]);
            }

            // Update pagination state
            if (snapshot.docs.length > 0) {
                setLastHistoryDoc(snapshot.docs[snapshot.docs.length - 1]);
            }
            setHasMoreHistory(snapshot.docs.length === 50);
        } catch (error) {
            console.error('Error loading history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadMoreHistory = () => {
        loadHistory(false);
    };

    /**
     * Add a new grocery item
     */
    const addItem = async (name, createdBy) => {
        try {
            await addDoc(collection(db, 'groceryItems'), {
                name: name.trim(),
                checked: false,
                checkedAt: null,
                checkedBy: null,
                createdAt: serverTimestamp(),
                createdBy: createdBy,
                order: 0
            });
        } catch (error) {
            console.error('Error adding grocery item:', error);
            throw error;
        }
    };

    /**
     * Check off a grocery item
     */
    const checkItem = async (itemId, userId) => {
        try {
            const itemRef = doc(db, 'groceryItems', itemId);
            await updateDoc(itemRef, {
                checked: true,
                checkedAt: serverTimestamp(),
                checkedBy: userId
            });
            // Reload history to include newly checked item
            loadHistory(true);
        } catch (error) {
            console.error('Error checking grocery item:', error);
            throw error;
        }
    };

    /**
     * Uncheck a grocery item (move back to active)
     */
    const uncheckItem = async (itemId) => {
        try {
            const itemRef = doc(db, 'groceryItems', itemId);
            await updateDoc(itemRef, {
                checked: false,
                checkedAt: null,
                checkedBy: null
            });
            // Reload history to remove unchecked item
            loadHistory(true);
        } catch (error) {
            console.error('Error unchecking grocery item:', error);
            throw error;
        }
    };

    /**
     * Delete a grocery item
     */
    const deleteItem = async (itemId) => {
        try {
            await deleteDoc(doc(db, 'groceryItems', itemId));
        } catch (error) {
            console.error('Error deleting grocery item:', error);
            throw error;
        }
    };

    return {
        activeItems,
        historyItems,
        loading,
        historyLoading,
        hasMoreHistory,
        addItem,
        checkItem,
        uncheckItem,
        deleteItem,
        loadMoreHistory
    };
};
